import { useCallback, useEffect, useRef, useState } from 'react';
import { DashboardSession } from '../../../context/DashboardSessionContext';
import { CafDashboard, CafDashboardWindow, getCafDashboard } from '../../../api/cafCorpus';

const POLL_MS = 60_000;

const cancelled = (error: unknown, signal: AbortSignal) => {
  const value = error as { code?: string; name?: string };
  return signal.aborted || value?.code === 'ERR_CANCELED' || value?.name === 'CanceledError' || value?.name === 'AbortError';
};

/** Poll shared SQL snapshots while keeping last-ready values within this auth session. */
export const useSnapshot = (days: CafDashboardWindow, session: DashboardSession) => {
  const [result, setResult] = useState<{ owner: DashboardSession; days: CafDashboardWindow; response: CafDashboard | null; failed: boolean; loading: boolean }>(
    { owner: session, days, response: null, failed: false, loading: false });
  const refreshRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    let active = true;
    let generation = 0;
    let timer: number | undefined;
    let controller: AbortController | null = null;
    const currentOwner = () => session.active && (session.isCurrent?.() ?? true);
    const clearTimer = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      timer = undefined;
    };
    const schedule = () => {
      clearTimer();
      if (active && document.visibilityState === 'visible') timer = window.setTimeout(load, POLL_MS);
    };
    const load = async () => {
      if (!active || !currentOwner() || document.visibilityState !== 'visible') return;
      clearTimer();
      generation += 1;
      const mine = generation;
      controller?.abort();
      const owned = new AbortController();
      controller = owned;
      setResult(current => ({ owner: session, days, loading: true,
        response: current.owner === session && current.days === days ? current.response : null,
        failed: current.owner === session && current.days === days ? current.failed : false }));
      try {
        const next = await getCafDashboard(days, owned.signal, session.acceptsAuthorization);
        if (!active || !currentOwner() || mine !== generation) return;
        const previous = session.ready[days];
        if (next.status === 'ready' && (!previous || (next.generated_at &&
            (!previous.generated_at || Date.parse(next.generated_at) >= Date.parse(previous.generated_at))))) {
          session.ready[days] = next;
        }
        setResult({ owner: session, days, response: next, failed: false, loading: true });
      } catch (error) {
        if (!cancelled(error, owned.signal) && active && currentOwner() && mine === generation) {
          setResult(current => ({ ...current, owner: session, failed: true }));
        }
      } finally {
        if (active && currentOwner() && mine === generation) {
          setResult(current => ({ ...current, loading: false }));
          schedule();
        }
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearTimer();
        generation += 1;
        controller?.abort();
        setResult(current => ({ ...current, loading: false }));
      } else {
        void load();
      }
    };
    refreshRef.current = () => { void load(); };
    document.addEventListener('visibilitychange', onVisibility);
    void load();
    return () => {
      active = false;
      generation += 1;
      clearTimer();
      controller?.abort();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [days, session]);

  const refresh = useCallback(() => refreshRef.current(), []);
  const owned = result.owner === session && result.days === days;
  const response = owned && result.response?.window_days === days ? result.response : null;
  return { snapshot: session.ready[days] ?? response, response,
    loading: owned && result.loading, failed: owned && result.failed, refresh };
};
